<template>
    <div class="main-container">
        <v-container fluid class="edit-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('schedule') }}</h4>
            <div class="d-flex flex-wrap mt-2">
                <!-- CLASS_EXHIBITION -->
                <template v-if="record.event_class === Event.CLASS_EXHIBITION">
                    <span>{{ $t('schedule_info_exhibition') }}</span>
                    <!-- recurring schedule -->
                    <KpTooltip>
                        <template #activator="{ on }">
                            <ActionBtn
                                class="flex-grow-1 my-2"
                                style="width: 100%; min-width: 160px"
                                v-on="on"
                                @click="newRecurringSchedule()"
                            >
                                <KpIcon icon-cls="events" color="white" disabled-color="grey" left />
                                {{ $t('recurring_type_recurring') }}
                            </ActionBtn>
                        </template>
                        <span v-html="$t('recurring_type_recurring_info_text')" />
                    </KpTooltip>

                    <span class="mt-8" v-html="$t('event_new_single_schedule_exhibition_text')"></span>

                    <!-- single schedule -->
                    <KpTooltip>
                        <template #activator="{ on }">
                            <ActionBtn
                                class="flex-grow-1 my-2"
                                style="min-width: 160px"
                                v-on="on"
                                @click="newSingleSchedule()"
                            >
                                <KpIcon icon-cls="calendar" color="white" disabled-color="grey" left />
                                {{ $t('recurring_type_single') }}
                            </ActionBtn>
                        </template>
                        <span v-html="$t('recurring_type_single_info_text')" />
                    </KpTooltip>
                </template>
                <!-- CLASS_TOURNEE -->
                <template v-else-if="record.event_class === Event.CLASS_TOURNEE">
                    <span>{{ $t('schedule_info_tournee') }}</span>
                    <!-- single schedule -->
                    <KpTooltip>
                        <template #activator="{ on }">
                            <ActionBtn
                                class="flex-grow-1 ma-2"
                                style="min-width: 160px"
                                v-on="on"
                                @click="newSingleSchedule()"
                            >
                                <KpIcon icon-cls="calendar" color="white" disabled-color="grey" left />
                                {{ $t('recurring_type_single_tournee') }}
                            </ActionBtn>
                        </template>
                        <span v-html="$t('recurring_type_single_tournee_info_text')" />
                    </KpTooltip>
                </template>
                <!-- all other event types (not CLASS_EXHIBITION && not CLASS_TOURNEE) -->
                <template v-else>
                    <span>{{ $t('schedule_info') }}</span>
                    <!-- single schedule -->
                    <KpTooltip>
                        <template #activator="{ on }">
                            <ActionBtn
                                class="flex-grow-1 ma-2"
                                style="min-width: 160px"
                                v-on="on"
                                @click="newSingleSchedule()"
                            >
                                <KpIcon icon-cls="calendar" color="white" disabled-color="grey" left />
                                {{ $t('recurring_type_single') }}
                            </ActionBtn>
                        </template>
                        <span v-html="$t('recurring_type_single_info_text')" />
                    </KpTooltip>

                    <!-- recurring schedule -->
                    <KpTooltip>
                        <template #activator="{ on }">
                            <ActionBtn
                                class="flex-grow-1 ma-2"
                                style="min-width: 160px"
                                v-on="on"
                                @click="newRecurringSchedule()"
                            >
                                <KpIcon icon-cls="events" color="white" disabled-color="grey" left />
                                {{ $t('recurring_type_recurring') }}
                            </ActionBtn>
                        </template>
                        <span v-html="$t('recurring_type_recurring_info_text')" />
                    </KpTooltip>
                </template>
            </div>

            <div v-if="openingHoursVisible">
                <h4 class="mt-5 mb-2">{{ $t('opening_hours') }}</h4>
                <p class="tgk-paragraph-text">{{ $t('opening_hours_event_tip') }}</p>
                <v-textarea
                    :value="record.opening_hours"
                    dense
                    :placeholder="$t('placeholder_schedule_opening_hours')"
                    rows="8"
                    outlined
                    hide-details="auto"
                    :counter="250"
                    :rules="[getRule('maxLength')(250)]"
                    @input="onInput('opening_hours', $event)"
                />
                <div class="d-flex">
                    <KpIcon icon-cls="info" color="grey" left size="24" style="width: 24px" />
                    <p class="tgk-paragraph-text" style="flex: 1">{{ $t('opening_hours_event_info') }}</p>
                </div>
            </div>
        </v-container>
        <div class="info-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('deine_erfassten_termine') }}</h4>
            <ScheduleCardList
                ref="scheduleCardList"
                :event="record"
                @edit="showEditDialog"
                @duplicate="duplicateSchedule"
                @deleted="afterScheduleDelete"
            />
        </div>
        <ScheduleEditDialog
            v-if="scheduleToEdit"
            :record="scheduleToEdit"
            :event="record"
            :value="!!scheduleToEdit"
            @input="scheduleToEdit = $event"
            @saved="scheduleSaved"
            @cancelled="resetSchedule"
        />
    </div>
</template>

<script>
import { Event } from '@/models/Event';
import { Schedule } from '@/models/Schedule';
import { mapState } from 'vuex';
import ScheduleEditDialog from '@/components/schedule/EditDialog';
import ScheduleCardList from '@/components/event/ScheduleCardList';
import { instance as api } from '@/helpers/api';
import getRulesMixin from '@/mixins/get-form-rules';

export default {
    name: 'EventEditSchedule',
    components: {
        ScheduleEditDialog,
        ScheduleCardList,
    },
    mixins: [getRulesMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
    },

    data() {
        return {
            scheduleToEdit: null,
        };
    },

    computed: {
        ...mapState('user', {
            user: 'record',
        }),
        openingHoursVisible() {
            return [Event.CLASS_EXHIBITION].includes(this.record.event_class);
        },
    },

    watch: {
        record: {
            handler(newValue, oldValue) {
                if (newValue?.getId() !== oldValue?.getId()) {
                    this.$refs.scheduleCardList?.loadSchedules();
                }
            },
            deep: true,
        },
    },

    async mounted() {
        this.$emit('input', true);
    },

    created() {
        this.Event = Event;
    },

    methods: {
        onInput(field, value) {
            this.record.set(field, value);
        },

        newSingleSchedule() {
            const schedule = new Schedule({
                event_id: this.record.getId(),
                recurring_type: Schedule.RECURRING_TYPE_SINGLE,
                recur_every_n: null,
            });
            this.showEditDialog(schedule);
        },

        newRecurringSchedule() {
            const schedule = new Schedule({
                event_id: this.record.getId(),
                recurring_type: null,
                recur_every_n: 1,
            });
            this.showEditDialog(schedule);
        },

        async duplicateSchedule(schedule) {
            const response = await api().post('/Schedule/duplicate', {
                id: schedule.getId(),
            });
            this.$refs.scheduleCardList?.loadSchedules();
            const dupe = new Schedule(response.data);
            this.scheduleToEdit = dupe;
        },

        showEditDialog(schedule) {
            this.scheduleToEdit = schedule;
        },

        async afterScheduleDelete(schedule) {
            this.$emit('reload-event');
        },

        async scheduleSaved(schedule) {
            if (!schedule) {
                return;
            }

            this.$refs.scheduleCardList?.loadSchedules();
            this.$emit('reload-event');
        },

        resetSchedule(schedule) {
            schedule.reset();
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-areas: 'edit info';
    grid-template-rows: auto;
    grid-template-columns: minmax(210px, 600px) minmax(440px, 1fr);
}
.edit-panel {
    grid-area: edit;
}
.info-panel {
    grid-area: info;
    background-color: var(--toolbar-bg-color);
}
</style>
